/**
 * SDK 配置管理器
 *
 * v2.0: 增强配置选项，支持更多自定义配置
 *
 * 集中管理 SDK 的全局配置，避免硬编码
 * 所有 API 类和服务都应该通过这个管理器获取配置
 */

import type { TaskAPIConfig } from './TaskAPIConfig'
import type { PaymentModuleConfig } from '../../types/config'

/**
 * 配置更新监听器
 */
export type ConfigUpdateListener = (config: SDKRuntimeConfig) => void

/**
 * SDK 运行时配置
 */
export interface SDKRuntimeConfig {
  /** 软件 ID（必须由使用方提供） */
  softId: string
  /** 存储 key 前缀（可选，默认 'csd_'） */
  storagePrefix?: string
  /** 应用版本（可选，默认 '1.0.0.0'） */
  version?: string
  /** 操作系统类型: 1-Web, 2-iOS, 3-Android（可选，默认 1） */
  os?: number
  /** 是否启用推广参数（可选，默认 true） */
  enablePromotionParams?: boolean
  /** 微信登录跳转 URL */
  wxLoginUrl?: string
  /** 微信登录/支付域名 */
  wxLoginAndPayDomain?: string
  /** 小程序标识 */
  applet?: string
  /** WeChat mini program virtual payment env: 0 production, 1 sandbox */
  virtualPayEnv?: string | number
  /** TaskAPI 配置（可选） */
  taskAPI?: Partial<TaskAPIConfig>
  /** 自定义端点映射（可选） */
  endpoints?: Record<string, string>
  /** 移动端支付跳转 URL */
  mobilePayUrl?: string
  /** 微信移动端支付跳转 URL */
  wxMobilePayUrl?: string
  /** 原生支付跳转回调（小程序/App） */
  onNativePayRedirect?: (orderId: number, type: 'mini' | 'app') => void
  /** 支付模块配置 */
  payment?: PaymentModuleConfig
}

/**
 * 默认配置值
 */
const DEFAULT_VALUES = {
  storagePrefix: 'csd_',
  version: '1.0.0.0',
  os: 1,
  enablePromotionParams: true
}

// 全局配置实例
let globalConfig: SDKRuntimeConfig = {
  softId: '', // 空字符串，强制使用方提供
  ...DEFAULT_VALUES
}

// 配置更新监听器列表
const configListeners: ConfigUpdateListener[] = []

/**
 * 验证配置
 *
 * @param config - 配置对象
 * @throws {Error} 如果配置无效
 */
export function validateSDKConfig(config: Partial<SDKRuntimeConfig>): void {
  // 验证必需字段
  if (config.softId === undefined || config.softId === null) {
    throw new Error('softId 是必需的配置项')
  }

  if (typeof config.softId !== 'string' || config.softId.trim() === '') {
    throw new Error('softId 必须是非空字符串')
  }

  // 验证可选字段类型
  if (config.storagePrefix !== undefined && typeof config.storagePrefix !== 'string') {
    throw new Error('storagePrefix 必须是字符串')
  }

  if (config.version !== undefined && typeof config.version !== 'string') {
    throw new Error('version 必须是字符串')
  }

  if (config.os !== undefined) {
    if (typeof config.os !== 'number' || ![1, 2, 3, 4, 5].includes(config.os)) {
      throw new Error(
        'os 必须是 1（Web/自动检测）、2（iOS）、3（Android）、4（PC Web）或 5（H5 移动端）'
      )
    }
  }

  if (
    config.enablePromotionParams !== undefined &&
    typeof config.enablePromotionParams !== 'boolean'
  ) {
    throw new Error('enablePromotionParams 必须是布尔值')
  }

  if (config.wxLoginUrl !== undefined && typeof config.wxLoginUrl !== 'string') {
    throw new Error('wxLoginUrl 必须是字符串')
  }

  if (config.wxLoginAndPayDomain !== undefined && typeof config.wxLoginAndPayDomain !== 'string') {
    throw new Error('wxLoginAndPayDomain 必须是字符串')
  }

  if (config.applet !== undefined && typeof config.applet !== 'string') {
    throw new Error('applet 必须是字符串')
  }

  if (
    config.virtualPayEnv !== undefined &&
    typeof config.virtualPayEnv !== 'string' &&
    typeof config.virtualPayEnv !== 'number'
  ) {
    throw new Error('virtualPayEnv must be a string or number')
  }

  if (config.mobilePayUrl !== undefined && typeof config.mobilePayUrl !== 'string') {
    throw new Error('mobilePayUrl 必须是字符串')
  }

  if (config.wxMobilePayUrl !== undefined && typeof config.wxMobilePayUrl !== 'string') {
    throw new Error('wxMobilePayUrl 必须是字符串')
  }

  if (
    config.onNativePayRedirect !== undefined &&
    typeof config.onNativePayRedirect !== 'function'
  ) {
    throw new Error('onNativePayRedirect 必须是函数')
  }

  if (config.payment !== undefined) {
    if (typeof config.payment !== 'object' || config.payment === null) {
      throw new Error('payment 必须是对象')
    }

    const sdk360 = config.payment.sdk360
    if (sdk360 !== undefined) {
      if (typeof sdk360 !== 'object' || sdk360 === null) {
        throw new Error('payment.sdk360 必须是对象')
      }
      if (sdk360.enabled !== undefined && typeof sdk360.enabled !== 'boolean') {
        throw new Error('payment.sdk360.enabled 必须是布尔值')
      }
      if (sdk360.appid !== undefined && typeof sdk360.appid !== 'string') {
        throw new Error('payment.sdk360.appid 必须是字符串')
      }
      if (sdk360.qid !== undefined && typeof sdk360.qid !== 'number') {
        throw new Error('payment.sdk360.qid 必须是数字')
      }
      if (sdk360.mid !== undefined && typeof sdk360.mid !== 'string') {
        throw new Error('payment.sdk360.mid 必须是字符串')
      }
      if (sdk360.m2 !== undefined && typeof sdk360.m2 !== 'string') {
        throw new Error('payment.sdk360.m2 必须是字符串')
      }
      if (sdk360.sdkUrl !== undefined && typeof sdk360.sdkUrl !== 'string') {
        throw new Error('payment.sdk360.sdkUrl 必须是字符串')
      }
    }
  }

  if (config.endpoints !== undefined) {
    if (typeof config.endpoints !== 'object' || config.endpoints === null) {
      throw new Error('endpoints 必须是对象')
    }
  }
}

/**
 * 初始化 SDK 配置
 * 应该在 SDK 初始化时调用
 *
 * @param config - 配置对象
 * @throws {Error} 如果配置无效
 */
export function initSDKConfig(config: Partial<SDKRuntimeConfig>): void {
  // 验证配置
  validateSDKConfig(config)

  // 合并配置（用户配置优先）
  globalConfig = {
    softId: config.softId!,
    storagePrefix: config.storagePrefix ?? DEFAULT_VALUES.storagePrefix,
    version: config.version ?? DEFAULT_VALUES.version,
    os: config.os ?? DEFAULT_VALUES.os,
    enablePromotionParams: config.enablePromotionParams ?? DEFAULT_VALUES.enablePromotionParams,
    wxLoginUrl: config.wxLoginUrl,
    wxLoginAndPayDomain: config.wxLoginAndPayDomain,
    applet: config.applet,
    virtualPayEnv: config.virtualPayEnv,
    taskAPI: config.taskAPI,
    endpoints: config.endpoints,
    mobilePayUrl: config.mobilePayUrl,
    wxMobilePayUrl: config.wxMobilePayUrl,
    onNativePayRedirect: config.onNativePayRedirect,
    payment: config.payment
  }

  // 通知监听器
  notifyConfigUpdate()
}

/**
 * 更新 SDK 配置
 *
 * @param config - 部分配置对象
 * @throws {Error} 如果配置无效
 */
export function updateSDKConfig(config: Partial<SDKRuntimeConfig>): void {
  // 验证配置（如果提供了 softId）
  if (config.softId !== undefined) {
    validateSDKConfig({ softId: config.softId })
  }

  // 合并配置
  globalConfig = {
    ...globalConfig,
    ...config
  }

  // 通知监听器
  notifyConfigUpdate()
}

/**
 * 获取当前 SDK 配置
 */
export function getSDKConfig(): SDKRuntimeConfig {
  return globalConfig
}

/**
 * 获取 soft_id
 */
export function getSoftId(): string {
  if (!globalConfig.softId) {
    console.warn(
      '[SDKConfigManager] softId is not configured. Please call initSDKConfig() with a valid softId.'
    )
  }
  return globalConfig.softId
}

/**
 * 获取存储 key 前缀
 */
export function getStoragePrefix(): string {
  return globalConfig.storagePrefix ?? DEFAULT_VALUES.storagePrefix
}

/**
 * 获取带前缀的存储 key
 */
export function getStorageKey(key: string): string {
  return `${globalConfig.storagePrefix ?? DEFAULT_VALUES.storagePrefix}${key}`
}

/**
 * 获取应用版本
 */
export function getVersion(): string {
  return globalConfig.version || DEFAULT_VALUES.version
}

/**
 * 获取操作系统类型
 */
export function getOS(): number {
  return globalConfig.os ?? DEFAULT_VALUES.os
}

/**
 * 是否启用推广参数
 */
export function isPromotionParamsEnabled(): boolean {
  return globalConfig.enablePromotionParams ?? DEFAULT_VALUES.enablePromotionParams
}

/**
 * 获取微信登录跳转 URL
 */
export function getWxLoginUrl(): string | undefined {
  return globalConfig.wxLoginUrl
}

/**
 * 获取微信登录/支付域名
 */
export function getWxLoginAndPayDomain(): string | undefined {
  return globalConfig.wxLoginAndPayDomain
}

/**
 * 获取小程序标识
 */
export function getApplet(): string | undefined {
  return globalConfig.applet
}

/**
 * Get WeChat mini program virtual payment env.
 */
export function getVirtualPayEnv(): string | number | undefined {
  return globalConfig.virtualPayEnv
}

/**
 * 获取 TaskAPI 配置
 */
export function getTaskAPIConfig(): Partial<TaskAPIConfig> | undefined {
  return globalConfig.taskAPI
}

/**
 * 获取自定义端点
 *
 * @param key - 端点 key
 * @returns 端点 URL，如果未配置则返回 undefined
 */
export function getEndpoint(key: string): string | undefined {
  return globalConfig.endpoints?.[key]
}

/**
 * 获取所有自定义端点
 */
export function getAllEndpoints(): Record<string, string> | undefined {
  return globalConfig.endpoints
}

/**
 * 获取移动端支付跳转 URL
 */
export function getMobilePayUrl(): string | undefined {
  return globalConfig.mobilePayUrl
}

/**
 * 获取微信移动端支付跳转 URL
 */
export function getWxMobilePayUrl(): string | undefined {
  return globalConfig.wxMobilePayUrl
}

/**
 * 获取原生支付跳转回调
 */
export function getNativePayRedirect():
  | ((orderId: number, type: 'mini' | 'app') => void)
  | undefined {
  return globalConfig.onNativePayRedirect
}

/**
 * 获取支付模块配置
 */
export function getPaymentConfig(): PaymentModuleConfig | undefined {
  return globalConfig.payment
}

/**
 * 获取 360 支付配置
 */
export function getSdk360PaymentConfig(): PaymentModuleConfig['sdk360'] | undefined {
  return globalConfig.payment?.sdk360
}

/**
 * 添加配置更新监听器
 *
 * @param listener - 监听器函数
 * @returns 取消监听的函数
 */
export function onConfigUpdate(listener: ConfigUpdateListener): () => void {
  configListeners.push(listener)

  // 返回取消监听的函数
  return () => {
    const index = configListeners.indexOf(listener)
    if (index > -1) {
      configListeners.splice(index, 1)
    }
  }
}

/**
 * 通知所有监听器配置已更新
 */
function notifyConfigUpdate(): void {
  const config = getSDKConfig()
  configListeners.forEach((listener) => {
    try {
      listener(config)
    } catch (error) {
      console.error('[SDKConfigManager] Error in config update listener:', error)
    }
  })
}

/**
 * 重置配置（主要用于测试）
 */
export function resetSDKConfig(): void {
  globalConfig = {
    softId: '',
    ...DEFAULT_VALUES
  }
  // 清空监听器
  configListeners.length = 0
}
